import React, { useState, useRef, useEffect } from 'react';
import classes from './styles.module.scss';

export default function RangeSlider({
  min = 0,
  max = 100,
  step = 1,
  value = [min, max],
  onChange,
  formatValue,
}) {
  const [localValue, setLocalValue] = useState(value);
  const sliderRef = useRef(null);
  const isDragging = useRef(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleMouseMove = (e) => {
    if (isDragging.current === null || !sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const percent = Math.max(
      0,
      Math.min(1, (e.clientX - rect.left) / rect.width)
    );
    const rawValue = min + percent * (max - min);
    const steppedValue = Math.round(rawValue / step) * step;

    const newValue = [...localValue];
    const index = isDragging.current;

    if (index === 0) {
      newValue[0] = Math.min(steppedValue, newValue[1]);
    } else {
      newValue[1] = Math.max(steppedValue, newValue[0]);
    }

    setLocalValue(newValue);
    onChange?.(newValue);
  };

  const handleMouseUp = () => {
    isDragging.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  const handleMouseDown = (index) => (e) => {
    e.preventDefault();
    isDragging.current = index;
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const leftPercent = ((localValue[0] - min) / (max - min)) * 100;
  const rightPercent = ((localValue[1] - min) / (max - min)) * 100;

  return (
    <div className={classes.RangeSlider}>
      <div className={classes.sliderContainer} ref={sliderRef}>
        <div className={classes.sliderTrack}>
          <div
            className={classes.sliderRange}
            style={{
              left: `${leftPercent}%`,
              width: `${rightPercent - leftPercent}%`,
            }}
          />
        </div>
        <div
          className={classes.thumb}
          style={{ left: `${leftPercent}%` }}
          onMouseDown={handleMouseDown(0)}
          role="slider"
          tabIndex={0}
          aria-valuenow={localValue[0]}
          aria-valuemin={min}
          aria-valuemax={max}
        >
          <div className={classes.thumbLabel}>
            {formatValue ? formatValue(localValue[0]) : localValue[0]}
          </div>
        </div>
        <div
          className={classes.thumb}
          style={{ left: `${rightPercent}%` }}
          onMouseDown={handleMouseDown(1)}
          role="slider"
          tabIndex={0}
          aria-valuenow={localValue[1]}
          aria-valuemin={min}
          aria-valuemax={max}
        >
          <div className={classes.thumbLabel}>
            {formatValue ? formatValue(localValue[1]) : localValue[1]}
          </div>
        </div>
      </div>
      <div className={classes.labels}>
        <span>{formatValue ? formatValue(min) : min}</span>
        <span>{formatValue ? formatValue(max) : max}</span>
      </div>
    </div>
  );
}
